import { Loading } from '@/core/ui/components/Loading';

import { useDeleteConfirmation, useFileViewer, useSection, useServices } from '@/app/providers';
import { SectionMultiSelect } from '@/modules/sections/ui/components/SectionMultiSelect';
import { useAbility } from '@/modules/identity/application/ability';
import { useCrudNotifications, useDateFormatter, useFileUpload } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { CommunityDocument, DocumentCategory } from '@/shared/types';
import type { UploadedFile } from '@yawi/types';
import { fetchAllPages } from '@yawi/infrastructure';
import { Download, Eye, File, FileText, Folder, Loader2, Search, Share2, Trash2, Upload, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { ShareDocumentModal } from '../molecules/ShareDocumentModal';

// Helper para inferir mimeType de la URL
const getMimeTypeFromUrl = (url: string): string => {
  // Si es una URL del CDN sin extensión, asumir PDF (tipo más común en documentos)
  if (url.includes('/cdn/files/') || url.includes('/api/v1/cdn/')) {
    return 'application/pdf';
  }

  const ext = url.split('.').pop()?.toLowerCase().split('?')[0];
  const mimeMap: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    mp4: 'video/mp4',
    mp3: 'audio/mpeg',
  };
  return mimeMap[ext || ''] || 'application/octet-stream';
};

export const DocumentLibrary = () => {
  const { t } = useI18n();
  const { formatDate } = useDateFormatter();
  const { confirmDelete } = useDeleteConfirmation();
  const { openFile, downloadFile, isLoading: isFileLoading } = useFileViewer();
  const { documentRepo, currentUser } = useServices();
  const { hasSections } = useSection();
  const ability = useAbility();
  const { withCreateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Documento',
  });

  // File upload hook
  const { upload, isUploading } = useFileUpload('documents');

  const [documents, setDocuments] = useState<CommunityDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

  // Upload Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDoc, setNewDoc] = useState<Partial<CommunityDocument>>({
    category: DocumentCategory.NOTICES
  });
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);

  // Share Modal
  const [shareModalFile, setShareModalFile] = useState<UploadedFile | null>(null);
  const [fileCache, setFileCache] = useState<Map<string, UploadedFile>>(new Map());

  const canManage = ability.can('manage', 'Document');

  // Check if user can share a specific file
  const canShare = (doc: CommunityDocument) => {
    return ability.can('manage', 'Document') || doc.uploadedBy === currentUser?.id;
  };

  // Los filtros viven en el servidor: cada cambio recarga el conjunto completo.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDocuments();
    }, 350);
    return () => clearTimeout(timer);
  }, [filter, categoryFilter]);

  // Solo la última búsqueda escribe el estado: recorrer todas las páginas de
  // una búsqueda amplia tarda más y, sin esto, pisaba los resultados nuevos.
  const requestIdRef = useRef(0);

  const fetchDocuments = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      // Búsqueda y categoría van al servidor y se recorren todas las páginas:
      // `getAllDocuments()` sólo traía la primera, así que el filtro local
      // truncaba la biblioteca sin avisar.
      const data = await fetchAllPages<CommunityDocument>(
        ({ page, limit }) =>
          documentRepo.getDocuments({
            page,
            limit,
            search: filter.trim() || undefined,
            category: categoryFilter === 'ALL' ? undefined : categoryFilter,
          }),
        { pageSize: 50 }
      );
      if (requestId !== requestIdRef.current) return;
      setDocuments(data);
    } catch (e) {
      console.error(e);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const result = await upload(file);
      if (result) {
        setUploadedFile(result);
        setNewDoc({ ...newDoc, url: result.url });
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDoc.title || !uploadedFile) return;

    try {
      await withCreateNotification(() => documentRepo.uploadDocument({
        title: newDoc.title ?? '',
        category: newDoc.category as DocumentCategory,
        url: uploadedFile.url,
        size: `${(uploadedFile.size / 1024 ).toFixed(2)} KB`,
        uploadedBy: currentUser?.name || 'Admin',
        fileId: uploadedFile.id,
        sectionIds: newDoc.sectionIds ?? [],
      }));

      // Cache the file for sharing later
      setFileCache(prev => new Map(prev).set(uploadedFile.id, uploadedFile));

      setIsModalOpen(false);
      setNewDoc({ category: DocumentCategory.NOTICES, title: '', url: '', sectionIds: [] });
      setUploadedFile(null);
      fetchDocuments();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const handleOpenShareModal = (doc: CommunityDocument) => {
    // If we have the file cached, use it
    if (doc.fileId && fileCache.has(doc.fileId)) {
      setShareModalFile(fileCache.get(doc.fileId)!);
    } else if (doc.fileId) {
      // Create a minimal UploadedFile from document data
      const file: UploadedFile = {
        id: doc.fileId,
        url: doc.url,
        filename: doc.title,
        originalName: doc.title,
        mimeType: 'application/pdf',
        size: parseFloat(doc.size || '0') * 1024 * 1024,
        category: 'documents',
        uploadedBy: doc.uploadedBy,
        uploadedAt: doc.uploadDate,
      };
      setShareModalFile(file);
    }
  };

  const handleShareModalClose = () => {
    setShareModalFile(null);
  };

  const handleFileUpdated = (updatedFile: UploadedFile) => {
    setFileCache(prev => new Map(prev).set(updatedFile.id, updatedFile));
    if (shareModalFile) {
      setShareModalFile(updatedFile);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    await confirmDelete({
      itemName: title,
      itemType: 'Documento',
      onConfirm: async () => {
        await withDeleteNotification(() => documentRepo.deleteDocument(id));
        fetchDocuments();
      }
    });
  };

  // `documents` ya viene filtrado y completo desde el servidor; no se vuelve a
  // filtrar aquí ni a recortar.
  const filteredDocs = documents;

  const getCategoryColor = (cat: DocumentCategory) => {
    switch (cat) {
      case DocumentCategory.BYLAWS: return 'bg-blue-100 text-blue-700';
      case DocumentCategory.FINANCIAL: return 'bg-emerald-100 text-emerald-700';
      case DocumentCategory.MINUTES: return 'bg-amber-100 text-amber-700';
      case DocumentCategory.INSURANCE: return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('docs.title')}</h2>
          <p className="text-slate-500 text-sm">Repositorio digital oficial del condominio.</p>
        </div>
        {canManage && (
          <Button onClick={() => setIsModalOpen(true)} icon={<Upload size={16}/>}>
            {t('docs.upload')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="relative flex-1">
          <input 
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
            placeholder="Buscar archivo..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400"/>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <button 
            onClick={() => setCategoryFilter('ALL')}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${categoryFilter === 'ALL' ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
          >
            Todos
          </button>
          {Object.values(DocumentCategory).map(cat => (
            <button 
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${categoryFilter === cat ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
            >
              {t(`docs.cat.${cat.toLowerCase()}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <Loading />
      ) : filteredDocs.length === 0 ? (
        <div className="text-center p-12 border-2 border-dashed border-slate-200 rounded-xl">
          <Folder size={48} className="mx-auto text-slate-300 mb-2"/>
          <p className="text-slate-500">{t('docs.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDocs.map((doc) => (
            <div key={doc.id} className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all group flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-lg ${getCategoryColor(doc.category)}`}>
                  <FileText size={24} />
                </div>
                <div className="flex items-center gap-1">
                  {canShare(doc) && doc.fileId && (
                    <button
                      onClick={() => handleOpenShareModal(doc)}
                      className="hover:text-primary-500 transition-colors p-1"
                      title="Compartir"
                    >
                      <Share2 size={16} />
                    </button>
                  )}
                  {canManage && (
                    <button onClick={() => handleDelete(doc.id, doc.title)} className="hover:text-red-500 transition-colors p-1">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              
              <h4 className="font-bold text-slate-800 mb-1 line-clamp-2 flex-1" title={doc.title}>{doc.title}</h4>
              
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded">
                  {t(`docs.cat.${doc.category.toLowerCase()}`)}
                </span>
                <span className="text-xs text-slate-400">• {doc.size}</span>
              </div>

              <div className="mt-auto pt-4 border-t border-slate-50 flex justify-between items-center">
                <span className="text-xs text-slate-400">{formatDate(doc.uploadDate)}</span>
                <div className="flex items-center gap-1">
                  {doc.fileId ? (
                    <>
                      <button
                        onClick={() => openFile(doc.fileId!, doc.title, doc.mimeType || getMimeTypeFromUrl(doc.url))}
                        disabled={isFileLoading}
                        className="flex items-center gap-1 text-xs font-bold text-primary-600 hover:text-primary-800 transition-colors bg-primary-50 px-3 py-1.5 rounded-lg disabled:opacity-50 cursor-pointer"
                        title="Ver archivo"
                      >
                        <Eye size={14}/> Ver
                      </button>
                      <button
                        onClick={() => downloadFile(doc.fileId!, doc.title)}
                        disabled={isFileLoading}
                        className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors hover:bg-slate-100 px-2 py-1.5 rounded-lg disabled:opacity-50 cursor-pointer"
                        title="Descargar archivo"
                      >
                        <Download size={14}/>
                      </button>
                    </>
                  ) : (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs font-bold text-primary-600 hover:text-primary-800 transition-colors bg-primary-50 px-3 py-1.5 rounded-lg cursor-pointer"
                    >
                      <Download size={14}/> Descargar
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">{t('docs.upload.modal')}</h3>
              <button onClick={() => setIsModalOpen(false)}><X size={20} className="text-slate-400 hover:text-slate-600"/></button>
            </div>
            
            <form onSubmit={handleUpload} className="p-6 space-y-4 overflow-y-auto">
              <Input 
                label={t('docs.upload.title')}
                value={newDoc.title || ''}
                onChange={e => setNewDoc({...newDoc, title: e.target.value})}
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('docs.upload.category')}</label>
                <select 
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                  value={newDoc.category}
                  onChange={e => setNewDoc({...newDoc, category: e.target.value as any})}
                >
                  {Object.values(DocumentCategory).map(cat => (
                    <option key={cat} value={cat}>{t(`docs.cat.${cat.toLowerCase()}`)}</option>
                  ))}
                </select>
              </div>

              {hasSections && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Visibilidad
                  </label>
                  <SectionMultiSelect
                    value={newDoc.sectionIds ?? []}
                    onChange={(sectionIds) => setNewDoc({ ...newDoc, sectionIds })}
                    resourceLabel="documento"
                    idPrefix="document"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('docs.upload.file')}</label>
                <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 ${isUploading ? 'pointer-events-none opacity-50' : ''}`}>
                   {isUploading ? (
                     <div className="flex flex-col items-center justify-center">
                       <Loader2 className="w-6 h-6 mb-2 text-primary-500 animate-spin" />
                       <p className="text-xs text-slate-500">Subiendo archivo...</p>
                     </div>
                   ) : uploadedFile ? (
                     <div className="text-center">
                       <span className="text-emerald-600 font-bold text-sm">Archivo Cargado</span>
                       <p className="text-xs text-slate-400 mt-1">{uploadedFile.originalName}</p>
                     </div>
                   ) : (
                     <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <File className="w-6 h-6 mb-2 text-slate-400" />
                        <p className="text-xs text-slate-500">Click para seleccionar PDF</p>
                     </div>
                   )}
                   <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileSelect} disabled={isUploading} />
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => { setIsModalOpen(false); setUploadedFile(null); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" disabled={!uploadedFile || isUploading} isLoading={isUploading}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModalFile && (
        <ShareDocumentModal
          file={shareModalFile}
          onClose={handleShareModalClose}
          onUpdated={handleFileUpdated}
        />
      )}
    </div>
  );
};
